// Content Submission Form - standalone form for uploading content or submitting post links
// Extracted from ContentSubmissionDialog so it can be used both in a dialog and on a full page.

import DOMPurify from "dompurify";
import {
  ContentState,
  getAcceptedFileTypes,
  getContentTypeCategory,
  getPostTypeDisplayName,
  isFileTypeAccepted,
  isInstagramStories,
  useContentFields,
  useGetUploadUrlMutation,
  useSubmitContentMutation,
  type ContentField,
  type ContentRequirement,
  type ContentRequirementSocialAccount,
  type InstagramStory,
  type MediaInput,
} from "@/lib/queries/content";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@repo/ui";
import { AlertTriangle, ImagePlus, Loader2 } from "lucide-react";
import { useLocation } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useFileUploadQueue } from "@/lib/hooks/use-file-upload-queue";
import { hashUrlToSupportCode } from "@/lib/utils/support-code";
import { ContentFieldsForm } from "./content-fields-form";
import { ContentGuidelines, useContentGuidelines } from "./content-guidelines";
import {
  getContentSubmissionErrorMessage,
  type ContentSubmissionError,
} from "./content-submission-errors";
import { FileUploadArea } from "./file-upload-area";
import { FileDropSection } from "./file-drop-section";
import {
  InstagramInsightsBanner,
  getUnlinkedInstagramUsernames,
  isInstagramInsightsNotLinked,
} from "./instagram-insights-banner";
import { TikTokBrandedContentNotice } from "./tiktok-branded-content-notice";
import { YouTubeBrandConnectNotice } from "./youtube-brand-connect-notice";
import { TRPCClientError } from "@trpc/client";
import { UncheckedGuidelinesModal } from "./unchecked-guidelines-modal";
import { UploadedFilesList } from "./uploaded-files-list";

// ============================================================================
// Helpers
// ============================================================================

// Post types where the creator uploads a file (image/video) rather than providing a URL
const FILE_UPLOAD_POST_TYPES = new Set([
  "ADDITIONAL_IMAGES",
  "ADDITIONAL_VIDEOS",
  "INSTAGRAM_STORY",
]);

function isFileUploadPostType(pt: string): boolean {
  return FILE_UPLOAD_POST_TYPES.has(pt);
}

/** Map a post type string to its network identifier (e.g. "instagram", "tiktok"). */
function networkForPostType(pt: string | null): string | null {
  if (!pt) return null;
  const upper = pt.toUpperCase();
  if (upper.startsWith("INSTAGRAM") || upper === "INSTAGRAM")
    return "instagram";
  if (upper.startsWith("TIKTOK")) return "tiktok";
  if (upper.startsWith("YOUTUBE")) return "youtube";
  if (upper.startsWith("FACEBOOK")) return "facebook";
  if (upper.startsWith("PINTEREST")) return "pinterest";
  if (upper.startsWith("TWITTER")) return "twitter";
  return null;
}

// ============================================================================
// Types
// ============================================================================

export interface ContentSubmissionFormProps {
  requirement: ContentRequirement;
  organizationId: string;
  profileId: string;
  programId: number;
  secureUrl: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Aspirex client ID for scoping content fields to the correct brand */
  clientId?: string;
  /** Pre-selected Instagram Stories (from InstagramStoryModal) */
  selectedStories?: InstagramStory[];
  /** Instagram handle for story submissions (from story modal's account data) */
  instagramHandle?: string | null;
  /** Callback to open the Instagram Story selection modal (for flexible briefs) */
  onRequestStorySelection?: () => void;
  /** Whether content fields should be fetched (defaults to true) */
  enableContentFields?: boolean;
  /** Whether the project requires Instagram Insights — when false, story submissions allow file upload */
  requireInstagramInsights?: boolean;
  /** Rendered at the top of the form's drop area, so a file dropped on it uploads too (EPD-12704) */
  heading?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function ContentSubmissionForm({
  requirement,
  organizationId,
  profileId,
  programId,
  secureUrl,
  onSuccess,
  onCancel,
  clientId,
  selectedStories = [],
  instagramHandle,
  onRequestStorySelection,
  enableContentFields = true,
  requireInstagramInsights,
  heading,
}: ContentSubmissionFormProps) {
  const location = useLocation();
  const postType = requirement.deliverable?.postType ?? null;
  const rawRequiresApproval = requirement.deliverable?.requiresApproval;

  // When content has been approved (ACCEPTED state), the creator only needs
  // to submit the live post link — skip the upload/review phase entirely.
  const isApprovedAwaitingPostLink =
    requirement.state === ContentState.ACCEPTED;

  // Flexible brief detection: multiple accepted post types means the creator picks
  const acceptedPostTypes = requirement.acceptedPostTypes ?? [];
  const isFlexibleBrief = acceptedPostTypes.length > 1;

  // Invalid state: deliverable is FLEXIBLE but no accepted post types configured
  const isInvalidFlexible =
    postType?.toUpperCase() === "FLEXIBLE" && acceptedPostTypes.length === 0;

  // For flexible briefs, the creator selects the content type
  const [selectedPostType, setSelectedPostType] = useState<string | null>(null);

  // The effective post type: for flexible briefs use the selection, otherwise the deliverable.
  // Only substitute acceptedPostTypes[0] when the deliverable itself is FLEXIBLE (or has no
  // postType) — never override a concrete type like INSTAGRAM_REEL with a mismatched
  // requirement-level acceptedPostTypes entry (EPD-7293).
  const effectivePostType = isFlexibleBrief
    ? selectedPostType
    : acceptedPostTypes.length === 1 &&
        (!postType || postType.toUpperCase() === "FLEXIBLE")
      ? acceptedPostTypes[0]
      : postType;
  const postTypeDisplay = getPostTypeDisplayName(effectivePostType);
  const acceptedFileTypes = getAcceptedFileTypes(effectivePostType);

  // EPD-9024: Custom Work ("other") is a no-approval generic file submission — no
  // social account, no platform link, no approval copy, caption optional.
  const isCustomWork = (effectivePostType ?? "").toLowerCase() === "other";

  // Custom Work is non-reviewable (EPD-9024, PM decision): the backend coerces
  // requiresApproval to false for it, but enforce it here too so the approval
  // copy / guideline gating / submit label / payload can never leak for Custom
  // Work even if upstream data isn't strictly false.
  const requiresApproval = rawRequiresApproval && !isCustomWork;

  // Build the list of selectable post types for the dropdown
  const postTypeOptions = useMemo(() => {
    if (!isFlexibleBrief) return [];
    return acceptedPostTypes
      .filter((pt: string) => pt.toLowerCase() !== "other")
      .map((pt: string) => ({ value: pt, label: getPostTypeDisplayName(pt) }));
  }, [isFlexibleBrief, acceptedPostTypes]);

  // Check if this is an Instagram Story submission.
  const isStoryPostType = isInstagramStories(effectivePostType);
  const isStorySubmission =
    isStoryPostType && (!requiresApproval || isApprovedAwaitingPostLink);
  const hasSelectedStories = selectedStories.length > 0;

  // When insights aren't required, story submissions use file upload instead of story selection.
  // This applies both for direct submissions (no approval) and approved-awaiting-link state,
  // since the creator may not have Instagram connected to provide a link or select stories.
  const isStoryFileUpload = isStorySubmission && !requireInstagramInsights;

  // Image/video deliverables don't need a caption field
  const isImageOrVideoDeliverable =
    effectivePostType === "ADDITIONAL_IMAGES" ||
    effectivePostType === "ADDITIONAL_VIDEOS";

  // Caption/Overlay Text field config — centralized to avoid drift across render paths
  const captionField = {
    label: isStoryPostType ? "Overlay Text" : "Caption",
    placeholder: isStoryPostType
      ? "Add overlay text for your story..."
      : "Add a caption for your content...",
    isRequired: !isImageOrVideoDeliverable && !isStoryPostType && !isCustomWork,
  } as const;

  // TikTok branded content info
  const isTikTok = effectivePostType === "TIKTOK_VIDEO";
  const tiktokRequiresBrandedContent =
    requirement.tcmRequiresBrandedContentTag ?? false;
  const tiktokInviteLink = requirement.tcmInviteLink ?? null;
  const showTikTokNotice =
    isTikTok && tiktokRequiresBrandedContent && tiktokInviteLink;

  // YouTube BrandConnect campaign code — program-scoped, joined onto the
  // requirement at the resolver layer when set on program_metadata.
  const isYouTube = networkForPostType(effectivePostType) === "youtube";
  const youtubeBrandConnectCampaignCode =
    requirement.youtubeBrandConnectCampaignCode?.trim() || null;
  const showYouTubeBrandConnectNotice =
    isYouTube && Boolean(youtubeBrandConnectCampaignCode);

  // Check if Instagram Insights are linked for story submissions (skip when using file upload)
  const insightsNotLinked =
    isStorySubmission &&
    !isStoryFileUpload &&
    isInstagramInsightsNotLinked(requirement.socialAccounts);
  const unlinkedUsernames = insightsNotLinked
    ? getUnlinkedInstagramUsernames(requirement.socialAccounts)
    : [];

  // Determine submission mode
  const isRejected = requirement.state === ContentState.REJECTED;

  // Custom Work and Story file uploads always need the file-upload UI; parens
  // are required because `||` binds looser than `?:` (without them the ternary
  // condition would be just `isStoryFileUpload`).
  const needsFileUpload =
    isCustomWork || isStoryFileUpload
      ? true
      : isApprovedAwaitingPostLink
        ? false
        : isFlexibleBrief
          ? effectivePostType
            ? isFileUploadPostType(effectivePostType)
            : false
          : isFileUploadPostType(effectivePostType ?? "") ||
            (Boolean(requiresApproval) && !isStorySubmission);

  // Resolve the social account handle for the effective post type.
  const resolvedHandle = useMemo(() => {
    const network = networkForPostType(effectivePostType);

    // Only use the story-selection Instagram handle for Instagram submissions
    if (instagramHandle && network === "instagram") return instagramHandle;
    // Deliverable-level account name (set by the brand).
    // Skip for YouTube — accountName stores the display name (not a handle),
    // and the backend uses it as a YouTube Data API `forHandle` param which
    // requires a real channel handle (e.g. @username). Sending null lets the
    // backend skip the handle-based lookup.
    if (requirement.deliverable?.accountName && network !== "youtube")
      return requirement.deliverable.accountName;
    if (!network) return null;

    return (
      requirement.socialAccounts?.find(
        (a: ContentRequirementSocialAccount) =>
          a.networkIdentifier?.toLowerCase() === network,
      )?.username ?? null
    );
  }, [
    instagramHandle,
    requirement.deliverable?.accountName,
    requirement.socialAccounts,
    effectivePostType,
  ]);

  // Content fields - custom brand-configured fields for content submission
  const { data: contentFieldsData } = useContentFields(
    organizationId,
    profileId,
    programId,
    clientId,
    enableContentFields,
  );
  const contentFields = useMemo(() => {
    const fields = contentFieldsData?.contentFields ?? [];
    if (!effectivePostType) return fields;
    const applicableFields = fields.filter(
      (f: ContentField) =>
        !f.postTypes ||
        f.postTypes.length === 0 ||
        (f.postTypes as string[]).includes(effectivePostType),
    );
    return applicableFields.filter(
      (f: ContentField) =>
        !f.programIds ||
        f.programIds.length === 0 ||
        (f.programIds as number[]).includes(programId),
    );
  }, [contentFieldsData, effectivePostType, programId]);

  // Content guidelines state
  const {
    guidelines,
    setGuidelines,
    hasGuidelines,
    uncheckedGuidelines,
    checkedOffFlat,
  } = useContentGuidelines(requirement.deliverable?.checkoffGuidelines);

  // Support/reference code for error correlation — recomputed when the route changes
  const supportCode = useMemo(
    () => hashUrlToSupportCode(window.location.href),
    [location.href],
  );

  // Form state
  const [mode, setMode] = useState<"upload" | "link">(
    needsFileUpload ? "upload" : "link",
  );
  const [postLink, setPostLink] = useState("");
  const [caption, setCaption] = useState("");
  const [contentFieldValues, setContentFieldValues] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ContentSubmissionError | null>(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<{
    media?: MediaInput[];
    caption?: string;
    link?: string;
    storyIds?: string[];
  } | null>(null);

  // Mutations
  const getUploadUrlMutation = useGetUploadUrlMutation();
  const submitContentMutation = useSubmitContentMutation(secureUrl, programId);

  // Uploads run one at a time with progress and retry (EPD-12608)
  const getUploadUrl = useCallback(
    async (file: File) => {
      const { uploadUrl } = await getUploadUrlMutation.mutateAsync({
        organizationId,
        profileId,
        fileName: `${Date.now()}-${file.name}`,
        contentType: getContentTypeCategory(file),
      });
      return uploadUrl;
    },
    [organizationId, profileId, getUploadUrlMutation],
  );
  const {
    files: uploadedFiles,
    addFiles,
    retryFile,
    removeFile: handleRemoveFile,
    clearFiles,
    isUploading,
    hasUploadedFiles: hasSuccessfulUploads,
  } = useFileUploadQueue({
    getUploadUrl,
    source: "content-submission",
    context: { organizationId, profileId, programId },
  });

  // Handle content type selection for flexible briefs
  const handleContentTypeChange = useCallback(
    (value: string) => {
      setSelectedPostType(value);
      setError(null);
      clearFiles();
      setPostLink("");
      setCaption("");
      setContentFieldValues({});
      if (isFileUploadPostType(value)) {
        setMode("upload");
      } else {
        setMode("link");
      }
    },
    [clearFiles],
  );

  // Queue accepted files for upload
  const processFiles = useCallback(
    (files: FileList) => {
      if (files.length === 0) return;

      setError(null);

      const accepted: File[] = [];
      for (const file of Array.from(files)) {
        if (!isFileTypeAccepted(file, acceptedFileTypes)) {
          setError({
            message: `File type not accepted. Skipped "${file.name}".`,
            showReference: false,
          });
          continue;
        }
        accepted.push(file);
      }
      addFiles(accepted);
    },
    [acceptedFileTypes, addFiles],
  );

  // Core submission logic
  const performSubmission = useCallback(
    async (params: {
      media?: MediaInput[];
      caption?: string;
      link?: string;
      storyIds?: string[];
      creatorGuidelineCheckoff?: boolean[];
    }) => {
      const contentReviewId =
        typeof requirement.id === "string"
          ? parseInt(requirement.id, 10)
          : Number.isFinite(requirement.id)
            ? Math.trunc(requirement.id)
            : NaN;
      if (!Number.isInteger(contentReviewId)) {
        throw new Error("Invalid content review ID");
      }

      const externalPostProjectId = requirement.backendServerProjectId;
      if (
        externalPostProjectId === null ||
        externalPostProjectId === undefined
      ) {
        throw new Error("Missing project ID");
      }

      // Guard: block submission when no account handle is resolvable for the expected network.
      // resolvedHandle already covers all handle sources in priority order:
      //   1. instagramHandle (story modal selection)
      //   2. deliverable.accountName (live read of SocialPostDeliverable.account entity)
      //   3. socialAccounts[].username
      // If resolvedHandle is null, fall back to checking socialAccounts for account existence
      // (covers platforms like YouTube where username may be null but the account still exists).
      const expectedNetwork = networkForPostType(effectivePostType);
      if (expectedNetwork && !resolvedHandle) {
        const hasAccount = requirement.socialAccounts?.some(
          (a: ContentRequirementSocialAccount) =>
            a.networkIdentifier?.toLowerCase() === expectedNetwork,
        );
        if (!hasAccount) {
          throw new Error(
            `No connected ${expectedNetwork} account was found for this deliverable. Please connect your account and try again.`,
          );
        }
      }

      const visibleFieldIds = new Set(
        contentFields.map((f: ContentField) => String(f.id)),
      );
      const fieldValues: Record<string, string> = {};
      for (const [key, val] of Object.entries(contentFieldValues)) {
        if (val !== "" && visibleFieldIds.has(key)) fieldValues[key] = val;
      }

      await submitContentMutation.mutateAsync({
        organizationId,
        profileId,
        externalPostProjectId,
        contentReviewId,
        programId,
        media: params.media ?? null,
        caption: params.caption ?? null,
        link: params.link ?? null,
        storyIds: params.storyIds ?? null,
        creatorGuidelineCheckoff: params.creatorGuidelineCheckoff ?? null,
        uploadedPostType: effectivePostType?.toLowerCase() ?? null,
        handle: resolvedHandle,
        contentFieldValues:
          Object.keys(fieldValues).length > 0 ? fieldValues : null,
      });
    },
    [
      requirement,
      organizationId,
      profileId,
      programId,
      effectivePostType,
      resolvedHandle,
      contentFields,
      contentFieldValues,
      submitContentMutation,
    ],
  );

  // Handle submission with guideline check
  const handleSubmit = useCallback(async () => {
    setError(null);

    if (isFlexibleBrief && !selectedPostType) {
      setError({
        message: "Please select a content type",
        showReference: false,
      });
      return;
    }

    const missingFields = contentFields
      .filter(
        (f: ContentField) =>
          f.isMandatory && !contentFieldValues[String(f.id)]?.trim(),
      )
      .map((f: ContentField) => f.name);
    if (missingFields.length > 0) {
      setError({
        message: `Please fill in required fields: ${missingFields.join(", ")}`,
        showReference: false,
      });
      return;
    }

    let media: MediaInput[] | undefined;
    let submissionCaption: string | undefined;
    let link: string | undefined;
    let storyIds: string[] | undefined;

    if (isStoryFileUpload) {
      // Story file upload mode — submit uploaded files as media
      const successfulUploads = uploadedFiles.filter(
        (f) => f.status === "done" && f.url,
      );
      if (successfulUploads.length === 0) {
        setError({
          message: "Please upload at least one file",
          showReference: false,
        });
        return;
      }
      media = successfulUploads.map((f) => {
        const category = getContentTypeCategory(f.file);
        const mediaType = category === "file" ? "document" : category;
        return {
          url: f.url,
          mediaType,
          contentType: f.file.type || null,
          previewUrl: f.url,
          size: f.file.size,
        };
      });
      submissionCaption = caption || undefined;
    } else if (isStorySubmission && hasSelectedStories) {
      storyIds = selectedStories
        .map((story) => story.mediaId)
        .filter((id): id is string => id != null);
      if (storyIds.length === 0) {
        setError({ message: "No valid story IDs found", showReference: false });
        return;
      }
      submissionCaption = caption || undefined;
    } else if (mode === "upload") {
      const successfulUploads = uploadedFiles.filter(
        (f) => f.status === "done" && f.url,
      );
      if (successfulUploads.length === 0) {
        setError({
          message: "Please upload at least one file",
          showReference: false,
        });
        return;
      }

      media = successfulUploads.map((f) => {
        const category = getContentTypeCategory(f.file);
        const mediaType = category === "file" ? "document" : category;
        return {
          url: f.url,
          mediaType,
          contentType: f.file.type || null,
          previewUrl: f.url,
          size: f.file.size,
        };
      });

      if (effectivePostType === "ADDITIONAL_VIDEOS") {
        const nonVideo = media.filter((m) => m.mediaType !== "video");
        if (nonVideo.length > 0) {
          setError({
            message:
              "This deliverable requires video files only. Please remove non-video files and try again.",
            showReference: false,
          });
          return;
        }
      } else if (effectivePostType === "ADDITIONAL_IMAGES") {
        const nonImage = media.filter((m) => m.mediaType !== "image");
        if (nonImage.length > 0) {
          setError({
            message:
              "This deliverable requires image files only. Please remove non-image files and try again.",
            showReference: false,
          });
          return;
        }
      }

      if (
        !isImageOrVideoDeliverable &&
        !isStoryPostType &&
        !isCustomWork &&
        !caption.trim()
      ) {
        setError({ message: "Please enter a caption", showReference: false });
        return;
      }

      submissionCaption = caption || undefined;
    } else {
      if (!postLink.trim()) {
        setError({ message: "Please enter a post link", showReference: false });
        return;
      }
      link = postLink.trim();
    }

    // Check for unchecked guidelines — skip when submitting the live link after approval
    if (
      requiresApproval &&
      !isApprovedAwaitingPostLink &&
      uncheckedGuidelines.length > 0
    ) {
      setPendingSubmission({
        media,
        caption: submissionCaption,
        link,
        storyIds,
      });
      setShowConfirmModal(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await performSubmission({
        media,
        caption: submissionCaption,
        link,
        storyIds,
        creatorGuidelineCheckoff:
          hasGuidelines && requiresApproval && !isApprovedAwaitingPostLink
            ? checkedOffFlat
            : undefined,
      });

      onSuccess?.();
    } catch (err) {
      const isValidation =
        err instanceof TRPCClientError && err.data?.code === "BAD_REQUEST";
      console[isValidation ? "warn" : "error"]("Submit error:", err);
      const safeError =
        err instanceof Error || typeof err === "string" ? err : null;
      setError(getContentSubmissionErrorMessage(safeError));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    mode,
    uploadedFiles,
    postLink,
    caption,
    isImageOrVideoDeliverable,
    isStoryPostType,
    isCustomWork,
    requiresApproval,
    isApprovedAwaitingPostLink,
    uncheckedGuidelines,
    checkedOffFlat,
    hasGuidelines,
    isStorySubmission,
    isStoryFileUpload,
    hasSelectedStories,
    selectedStories,
    isFlexibleBrief,
    selectedPostType,
    contentFields,
    contentFieldValues,
    effectivePostType,
    performSubmission,
    onSuccess,
  ]);

  // Handle confirmed submission after guideline warning
  const handleConfirmedSubmit = useCallback(async () => {
    if (!pendingSubmission) return;

    setShowConfirmModal(false);
    setIsSubmitting(true);
    setError(null);

    try {
      await performSubmission({
        ...pendingSubmission,
        creatorGuidelineCheckoff: checkedOffFlat,
      });

      onSuccess?.();
    } catch (err) {
      const isValidation =
        err instanceof TRPCClientError && err.data?.code === "BAD_REQUEST";
      console[isValidation ? "warn" : "error"]("Submit error:", err);
      const safeError =
        err instanceof Error || typeof err === "string" ? err : null;
      setError(getContentSubmissionErrorMessage(safeError));
    } finally {
      setIsSubmitting(false);
      setPendingSubmission(null);
    }
  }, [pendingSubmission, checkedOffFlat, performSubmission, onSuccess]);

  const hasContentTypeSelected = !isFlexibleBrief || !!selectedPostType;

  // The two states that show an upload box. File drops on the section are only
  // accepted in these: anywhere else (link mode, story selection, no content type
  // yet) a dropped file would upload with nothing on screen to show it (EPD-12704).
  const showsStoryFileUpload = isStoryFileUpload && hasContentTypeSelected;
  const showsFileUpload =
    mode === "upload" &&
    !isStorySubmission &&
    !isStoryFileUpload &&
    hasContentTypeSelected;

  const canSubmit =
    !isSubmitting &&
    !isUploading &&
    !isInvalidFlexible &&
    hasContentTypeSelected &&
    ((isStoryFileUpload && hasSuccessfulUploads) ||
      (isStorySubmission && !isStoryFileUpload && hasSelectedStories) ||
      (mode === "upload" && hasSuccessfulUploads) ||
      (mode === "link" && postLink.trim()));

  // Submit button label
  const submitLabel = isSubmitting ? (
    <>
      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      Submitting...
    </>
  ) : isRejected ? (
    "Resubmit"
  ) : isStoryFileUpload ? (
    "Submit Story"
  ) : isStorySubmission && hasSelectedStories ? (
    `Send ${selectedStories.length} ${selectedStories.length === 1 ? "Story" : "Stories"}`
  ) : isApprovedAwaitingPostLink ? (
    "Submit Post Link"
  ) : requiresApproval ? (
    "Send for Review"
  ) : (
    "Submit"
  );

  return (
    <>
      {/* The whole form body is a drop target, not only the dashed box, so a file
          dropped anywhere in the Submit Content section uploads instead of the
          browser opening it in a new tab (EPD-12704). Drops only upload while an
          upload box is shown; otherwise they're just kept from opening the file. */}
      <FileDropSection
        className="space-y-4"
        onFilesSelected={processFiles}
        disabled={isSubmitting || !(showsStoryFileUpload || showsFileUpload)}
      >
        {heading}

        {/* Invalid flexible brief — no accepted post types configured */}
        {isInvalidFlexible && (
          <div className="flex gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              No valid content types are configured for this deliverable. Please
              contact the brand or support for assistance.
            </div>
          </div>
        )}

        {/* Rejection feedback (shown in dialog mode; the page shows its own alert) */}
        {isRejected && requirement.brandRejectionComment && (
          <div className="flex gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong>Brand Feedback:</strong>{" "}
              <span
                // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized with DOMPurify
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(requirement.brandRejectionComment),
                }}
              />
            </div>
          </div>
        )}

        {/* Content Type Selection for Flexible Briefs */}
        {isFlexibleBrief && (
          <div>
            <Label htmlFor="contentType">Content Type</Label>
            <Select
              value={selectedPostType ?? ""}
              onValueChange={handleContentTypeChange}
            >
              <SelectTrigger id="contentType" className="mt-2">
                <SelectValue placeholder="Select content type..." />
              </SelectTrigger>
              <SelectContent position="item-aligned">
                {postTypeOptions.map(
                  (option: { value: string; label: string }) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* TikTok Branded Content Notice */}
        {showTikTokNotice && (
          <TikTokBrandedContentNotice inviteLink={tiktokInviteLink} />
        )}

        {/* YouTube BrandConnect Campaign Code Notice */}
        {showYouTubeBrandConnectNotice && youtubeBrandConnectCampaignCode && (
          <YouTubeBrandConnectNotice
            campaignCode={youtubeBrandConnectCampaignCode}
          />
        )}

        {/* Instagram Insights Required Banner */}
        {insightsNotLinked && (
          <InstagramInsightsBanner accountUsernames={unlinkedUsernames} />
        )}

        {/* Content Guidelines with Checkoff */}
        {requiresApproval && hasGuidelines && !isApprovedAwaitingPostLink && (
          <ContentGuidelines guidelines={guidelines} onChange={setGuidelines} />
        )}

        {/* Story File Upload - shown when insights not required */}
        {showsStoryFileUpload && (
          <div className="space-y-4">
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>
                Upload a screenshot or screen recording of your Instagram Story.
              </li>
              <li>2GB file size limit</li>
            </ul>

            <FileUploadArea
              acceptedFileTypes={acceptedFileTypes}
              onFilesSelected={processFiles}
              disabled={isSubmitting}
            />

            <UploadedFilesList
              files={uploadedFiles}
              onRemove={handleRemoveFile}
              onRetry={retryFile}
            />

            <div>
              <Label htmlFor="caption">
                {captionField.label}
                {captionField.isRequired && (
                  <span className="text-destructive"> *</span>
                )}
              </Label>
              <Textarea
                id="caption"
                required={captionField.isRequired}
                placeholder={captionField.placeholder}
                value={caption}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setCaption(e.target.value)
                }
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Instagram Story Selection - shown for flexible briefs when no stories selected yet */}
        {isStorySubmission &&
          !isStoryFileUpload &&
          !hasSelectedStories &&
          hasContentTypeSelected &&
          onRequestStorySelection && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Select your Instagram Stories to submit to the brand.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={onRequestStorySelection}
              >
                <ImagePlus className="h-4 w-4 mr-2" />
                Select Stories
              </Button>
            </div>
          )}

        {/* Instagram Stories Preview */}
        {isStorySubmission && !isStoryFileUpload && hasSelectedStories && (
          <div className="space-y-4">
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>
                The brand has indicated you can post without uploading content
                for approval first.
              </li>
              <li>The selected stories below will be sent to the brand.</li>
            </ul>
            <div className="flex flex-wrap gap-2">
              {selectedStories.map((story) => (
                <div
                  key={story.key}
                  className="relative w-16 h-28 rounded-md overflow-hidden bg-muted"
                >
                  {story.imageUrl ? (
                    <img
                      src={story.imageUrl}
                      alt="Story"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      ?
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">
              {selectedStories.length}{" "}
              {selectedStories.length === 1 ? "story" : "stories"} selected
            </p>

            <div>
              <Label htmlFor="caption">
                {captionField.label}
                {captionField.isRequired && (
                  <span className="text-destructive"> *</span>
                )}
              </Label>
              <Textarea
                id="caption"
                required={captionField.isRequired}
                placeholder={captionField.placeholder}
                value={caption}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setCaption(e.target.value)
                }
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Upload mode - shown when content type requires file upload */}
        {showsFileUpload && (
          <div className="space-y-4">
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              {requiresApproval ? (
                <>
                  <li>
                    The brand requires you to send this content for approval
                    before posting.
                  </li>
                  <li>
                    After approval, set your {postTypeDisplay.toLowerCase()}{" "}
                    live and submit the link.
                  </li>
                </>
              ) : (
                <li>Upload your {postTypeDisplay.toLowerCase()} file below.</li>
              )}
              <li>2GB file size limit</li>
            </ul>

            <FileUploadArea
              acceptedFileTypes={acceptedFileTypes}
              onFilesSelected={processFiles}
              disabled={isSubmitting}
            />

            <UploadedFilesList
              files={uploadedFiles}
              onRemove={handleRemoveFile}
              onRetry={retryFile}
            />

            {!isImageOrVideoDeliverable && (
              <div>
                <Label htmlFor="caption">
                  {captionField.label}
                  {captionField.isRequired && (
                    <span className="text-destructive"> *</span>
                  )}
                </Label>
                <Textarea
                  id="caption"
                  required={captionField.isRequired}
                  placeholder={captionField.placeholder}
                  value={caption}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCaption(e.target.value)
                  }
                  className="mt-2"
                  rows={3}
                />
              </div>
            )}
          </div>
        )}

        {/* Link mode - shown when content type requires a live post URL */}
        {mode === "link" && !isStorySubmission && hasContentTypeSelected && (
          <div className="space-y-4">
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              {isApprovedAwaitingPostLink ? (
                <>
                  <li>Your content has been approved by the brand.</li>
                  <li>
                    Post your {postTypeDisplay.toLowerCase()} live, then paste
                    the link below.
                  </li>
                </>
              ) : (
                <>
                  <li>
                    The brand has indicated you can post without uploading
                    content for approval first.
                  </li>
                  <li>
                    Post the {postTypeDisplay.toLowerCase()} then submit via
                    Aspire and you're done.
                  </li>
                </>
              )}
            </ul>

            <div>
              <Label htmlFor="postLink">Link to Live {postTypeDisplay}</Label>
              <Input
                id="postLink"
                type="url"
                placeholder="https://..."
                value={postLink}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPostLink(e.target.value)
                }
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste the link to your published {postTypeDisplay.toLowerCase()}
              </p>
            </div>
          </div>
        )}

        {/* Content Fields - brand-configured custom fields */}
        {contentFields.length > 0 && hasContentTypeSelected && (
          <ContentFieldsForm
            fields={contentFields}
            values={contentFieldValues}
            onChange={setContentFieldValues}
          />
        )}

        {/* Error message */}
        {error && (
          <div className="flex gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="text-sm space-y-1">
              <p>{error.message}</p>
              {error.showReference && (
                <p className="font-mono text-xs opacity-75 select-all">
                  Reference: {supportCode}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        </div>
      </FileDropSection>

      {/* Confirmation Modal for Unchecked Guidelines */}
      <UncheckedGuidelinesModal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        uncheckedGuidelines={uncheckedGuidelines}
        onConfirm={handleConfirmedSubmit}
        onCancel={() => setPendingSubmission(null)}
      />
    </>
  );
}
